import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { MediaPost } from '@/lib/media';
import { formatCount } from '@/lib/media';
import VerifiedBadge from './VerifiedBadge';

interface FeaturedCarouselProps {
  videos: MediaPost[];
  certifiedArtists: Set<string>;
}

function FeaturedCarousel({ videos, certifiedArtists }: FeaturedCarouselProps) {
  const [index, setIndex] = useState(0);
  const timer = useRef<number | null>(null);

  const featured = useMemo(
    () => [...videos].sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, 5),
    [videos],
  );

  useEffect(() => {
    if (featured.length <= 1) return;
    timer.current = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % featured.length);
    }, 3000);
    return () => {
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [featured.length]);

  if (featured.length === 0) return null;

  const current = featured[index];

  const goTo = (i: number) => {
    setIndex((i + featured.length) % featured.length);
  };

  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading font-bold text-base md:text-lg text-foreground-50 flex items-center gap-2">
          <i className="ri-fire-line text-accent-400" />
          À la une
        </h2>
        <span className="text-xs text-foreground-400">Les contenus les plus regardés</span>
      </div>

      <div className="relative w-full h-[300px] md:h-[420px] rounded-lg overflow-hidden border border-background-200/70 bg-background-950">
        {current.thumbnail_url ? (
          <img
            src={current.thumbnail_url}
            alt={current.title}
            title={`${current.title} — ${current.artist_name || ''}`}
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-background-800 to-background-950" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/20" />

        {/* Contenu */}
        <div className="relative z-10 h-full flex flex-col justify-end p-6 md:p-10 max-w-3xl">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="inline-block px-3 py-1 rounded-full bg-background-950/50 backdrop-blur-sm text-xs font-semibold text-foreground-50">
              {current.category}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs text-foreground-50/90">
              <i className="ri-eye-line" />
              {formatCount(current.views)} vues
            </span>
          </div>
          <h3 className="font-heading font-bold text-xl md:text-3xl text-foreground-50 leading-tight mb-2">
            {current.title}
          </h3>
          {current.description && (
            <p className="text-sm text-foreground-50/80 leading-relaxed mb-4 line-clamp-2 hidden sm:block">
              {current.description}
            </p>
          )}
          <div className="flex items-center gap-4 flex-wrap">
            {current.artist_name && (
              <Link
                to={`/media/artiste/${encodeURIComponent(current.artist_name)}`}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-foreground-50 hover:text-primary-400 transition-colors"
              >
                <i className="ri-user-star-line text-accent-400" />
                {current.artist_name}
                {certifiedArtists.has(current.artist_name) && <VerifiedBadge />}
              </Link>
            )}
            {current.location && (
              <span className="inline-flex items-center gap-1.5 text-xs text-foreground-50/80">
                <i className="ri-map-pin-line text-accent-400" />
                {current.location}
              </span>
            )}
          </div>
        </div>

        {/* Flèches */}
        <button
          onClick={() => goTo(index - 1)}
          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors z-20"
          aria-label="Contenu précédent"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          onClick={() => goTo(index + 1)}
          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-background-950/40 backdrop-blur-sm text-foreground-50 hover:bg-background-950/70 transition-colors z-20"
          aria-label="Contenu suivant"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>

        {/* Points */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-20">
          {featured.map((v, i) => (
            <button
              key={v.id}
              onClick={() => goTo(i)}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === index ? 'w-6 bg-primary-500' : 'w-1.5 bg-foreground-50/60'
              }`}
              aria-label={`Aller au contenu ${i + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default FeaturedCarousel;